import { Button, Col, Row, Input } from "antd";
import _get from "lodash/get";
import _isEmpty from "lodash/isEmpty";
import PropTypes from "prop-types";
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { debounce } from "lodash";
import ReactQuill from "react-quill";
import DOMPurify from "dompurify";
import beautify from "js-beautify";
import CustomFormItem from "../../components/CustomFormItem/CustomFormItem";
import LabelWithDropDown from "../../components/LabelWithDropDown/LabelWithDropDown";
import { useLanguage } from "../../hooks";
import {
  LANGUAGE_CODES,
  FORM_FIELDS_LOOKUP,
  HELPER_TEXT_KEYS,
  BUILDING_TABS_KEYS,
  BUILDING_REQUIRED_FIELDS_LOOKUP,
  FORM_FIELDS_TYPE,
} from "../../utils/constants";
import { validateMarketingDescription, getFeatureFlags } from "../../utils/utils";
import AppModal from "../../components/AppModal/AppModal";
import "react-quill/dist/quill.snow.css";
import sourceIcon from "../../assets/images/source code icon.png";

const LandMarketingDescriptionEditor = ({
  viewOnlyMode,
  tooltip,
  label,
  templateName,
  form,
  templateList,
  parentId,
  tab,
  fieldName,
  auditPanelDrawerVisible,
  useRichTextEditor = true,
  ...props
}) => {
  const { t } = useTranslation();
  const [templateId, setTemplateId] = useState(null);
  const { language, languageStatus } = useLanguage();
  const { lookupList } = useSelector(state => state.helperText);
  const [modalVisible, setModalVisibility] = useState(false);
  const fieldsType = FORM_FIELDS_TYPE.regular;
  const [description, setDescription] = useState("");
  const [showHtml, setShowHtml] = useState(false);
  const FEATURE_FLAG = getFeatureFlags();
  const [htmlError, setHtmlError] = useState("");
  const [pendingTemplateId, setPendingTemplateId] = useState(null);
  const [beautifiedHtml, setBeautifiedHtml] = useState("");

  // ReactQuill ref so we can paste HTML back into the editor safely
  const quillRef = useRef(null);

  const handleLineBreak = useCallback(() => {
    const editor = quillRef.current?.getEditor?.();
    if (!editor) return;
    const range = editor.getSelection(true);
    if (!range) return;
    editor.insertText(range.index, "\n", "user");
    setTimeout(() => {
      editor.setSelection(range.index + 1, 0, "user");
      editor.focus();
    }, 0);
  }, []);

  const quillModules = useMemo(
    () => ({
      toolbar: {
        container: [
          ["bold", "italic"],
          [{ list: "ordered" }, { list: "bullet" }],
          ["link"],
          ["clean"],
          [{ script: "sub" }, { script: "super" }],
        ],
        handlers: {
          clean: handleLineBreak,
        },
      },
    }),
    [handleLineBreak],
  );

  useEffect(() => {
    const initializeDescription = async () => {
      const initialTemplateId = form.getFieldValue(["landInfo", "templateId"]);
      const initialLang = language.current;
      const langStatus = languageStatus;
      setTemplateId(initialTemplateId);
      if (langStatus !== "primary") {
        const translatedDescription = _get(
          form.getFieldsValue(),
          ["translations", initialLang, "descriptionTab", "marketingDescription"],
          "",
        );
        if (translatedDescription) {
          setDescription(translatedDescription);
        } else if (initialTemplateId) {
          await populateDescription(initialTemplateId);
        }
      } else {
        const primaryDescription = form.getFieldValue(["descriptionTab", "marketingDescription"]);
        if (primaryDescription) {
          setDescription(primaryDescription);
        } else if (initialTemplateId) {
          await populateDescription(initialTemplateId);
        }
      }
    };

    initializeDescription();
  }, [form, templateList, language.current, languageStatus]);

  useEffect(() => {
    const fetchDescriptionByLanguage = () => {
      const formValues = form.getFieldsValue();
      let translatedDescription;
      if (languageStatus !== "primary") {
        translatedDescription = _get(
          formValues,
          ["translations", language.current, "descriptionTab", "marketingDescription"],
          "",
        );
      } else {
        translatedDescription = _get(formValues, ["descriptionTab", "marketingDescription"], "");
      }
      setDescription(translatedDescription);
    };
    fetchDescriptionByLanguage();
  }, [language.current, languageStatus, form]);

  useEffect(() => {
    if (templateId) {
      const country = form.getFieldValue(["landInfo", "address", "country"]);
      const selectedTemplate = templateList.find(item => item.id === templateId);
      if (!selectedTemplate) return;
      const shouldOverride = _isEmpty(description);
      if (shouldOverride) {
        const marketingDescription = _get(
          selectedTemplate,
          `translations.${
            country === "Mexico" && language.current !== LANGUAGE_CODES.English.value
              ? LANGUAGE_CODES.Mexico.value
              : language.current
          }.template`,
          selectedTemplate.template,
        );
        setDescription(marketingDescription);
      }
    }
  }, [templateId]);

  const fetchTemplateById = async id => {
    try {
      const response = await fetch(`/api/templates/${id}`);
      if (!response.ok) throw new Error("Failed to fetch");
      const data = await response.json();
      return data;
    } catch (err) {
      return null;
    }
  };

  const populateDescription = async selectedTemplateId => {
    if (!selectedTemplateId) return;
    const country = form.getFieldValue(["landInfo", "address", "country"]);
    const selectedTemplate = await fetchTemplateById(selectedTemplateId);
    if (!selectedTemplate) return;
    const marketingDescription = _get(
      selectedTemplate,
      `translations.${
        country === "Mexico" && language.current !== LANGUAGE_CODES.English.value
          ? LANGUAGE_CODES.Mexico.value
          : language.current
      }.template`,
      selectedTemplate.template,
    );
    const obj = {};
    if (languageStatus !== "primary") {
      obj.translations = {
        [language.current]: { descriptionTab: { marketingDescription } },
      };
    } else {
      obj.descriptionTab = { marketingDescription };
    }
    form.setFieldsValue(obj);
    setDescription(marketingDescription);
    setTemplateId(selectedTemplateId);
  };

  const setTemplateDescription = newTemplateId => {
    if (_isEmpty(description)) {
      populateDescription(newTemplateId);
      setTemplateId(newTemplateId);
      form.setFieldsValue({
        landInfo: {
          ...form.getFieldValue("landInfo"),
          templateId: newTemplateId,
        },
      });
    } else {
      setPendingTemplateId(newTemplateId);
      setModalVisibility(true);
    }
  };
  const _onCancel = () => {
    setModalVisibility(false);

    const currentBuildingInfo = form.getFieldValue("landInfo") || {};
    form.setFieldsValue({ landInfo: { ...currentBuildingInfo, templateId } });
  };
  const _onConfirm = () => {
    if (pendingTemplateId) {
      const country = form.getFieldValue(["landInfo", "address", "country"]);
      const selectedTemplate = templateList.find(item => item.id === pendingTemplateId);
      if (!selectedTemplate) return;
      const marketingDescription = _get(
        selectedTemplate,
        `translations.${
          country === "Mexico" && language.current !== LANGUAGE_CODES.English.value
            ? LANGUAGE_CODES.Mexico.value
            : language.current
        }.template`,
        selectedTemplate.template,
      );
      const obj = {};
      if (languageStatus !== "primary") {
        obj.translations = {
          [language.current]: { descriptionTab: { marketingDescription } },
        };
      } else {
        obj.descriptionTab = { marketingDescription };
      }
      if (!form) return;

      const currentBuildingInfo = form.getFieldValue("landInfo") || {};
      // FIX: set landInfo (was buildingInfo in original code)
      form.setFieldsValue({
        landInfo: { ...currentBuildingInfo, templateId: pendingTemplateId },
        ...obj,
      });
      setTemplateId(pendingTemplateId);
      setDescription(marketingDescription);
      setPendingTemplateId(null);
      setModalVisibility(false);
    }
  };

  const handleEditorChange = value => {
    setDescription(value);

    const sanitized = DOMPurify.sanitize(value, {
      ALLOWED_ATTR: ["href", "target", "rel"],
      ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i,
    });

    if (sanitized !== value) {
      setHtmlError(t("Only basic HTML tags (bold, italic, lists, links, line breaks, super/sub script) are allowed."));
    } else {
      setHtmlError("");
    }

    const obj =
      languageStatus !== "primary"
        ? {
            translations: {
              [language.current]: {
                descriptionTab: { marketingDescription: sanitized },
              },
            },
          }
        : {
            descriptionTab: { marketingDescription: sanitized },
          };

    form.setFieldsValue(obj);
  };

  // When switching between source <-> rich, paste the HTML into Quill so Quill's internal
  // parser/Delta keeps sup/sub as formats. Relying on setting the value prop alone can
  // cause Quill to lose formats because it parses HTML differently.
  const handleSourceIconClick = () => {
    const enteringSource = !showHtml;

    if (enteringSource) {
      // Switching to source view: produce beautified HTML
      let raw = (description || "").trim();
      if (raw && !raw.startsWith("<p") && !raw.endsWith("</p>")) {
        raw = `<p>${raw}</p>`;
      }
      const sanitised = DOMPurify.sanitize(raw, {
        ALLOWED_ATTR: ["href", "target", "rel"],
      });
      const beautified = beautify.html(sanitised, {
        indent_size: 2,
        wrap_line_length: 80,
      });
      setBeautifiedHtml(beautified);
    } else {
      // Switching back to rich editor. Sanitize the source and paste into Quill so
      // Quill correctly maps <sup>/<sub> to the 'script' format and preserves them.
      const updatedHtml = DOMPurify.sanitize(beautifiedHtml || "", {
        ALLOWED_ATTR: ["href", "target", "rel"],
      });

      if (quillRef.current && quillRef.current.getEditor) {
        const editor = quillRef.current.getEditor();
        // Use Quill clipboard to let Quill convert HTML -> Delta correctly
        editor.clipboard.dangerouslyPasteHTML(updatedHtml);
        // Keep local state in sync with editor's HTML
        const editorHtml = editor.root.innerHTML;
        setDescription(editorHtml);

        const obj =
          languageStatus !== "primary"
            ? {
                translations: {
                  [language.current]: {
                    descriptionTab: { marketingDescription: editorHtml },
                  },
                },
              }
            : {
                descriptionTab: { marketingDescription: editorHtml },
              };

        form.setFieldsValue(obj);
      } else {
        setDescription(updatedHtml);
        const obj =
          languageStatus !== "primary"
            ? {
                translations: {
                  [language.current]: { descriptionTab: { marketingDescription: updatedHtml } },
                },
              }
            : { descriptionTab: { marketingDescription: updatedHtml } };
        form.setFieldsValue(obj);
      }
    }

    setShowHtml(enteringSource);
  };

  useEffect(() => {
    if (!showHtml) return;
    const raw = (description || "").trim();
    const wrapped = /^<p[^>]*>[\s\S]*<\/p>$/.test(raw) ? raw : `<p>${raw}</p>`;
    const sanitised = DOMPurify.sanitize(wrapped, {
      ALLOWED_ATTR: ["href", "target", "rel"],
    });
    const beautified = beautify.html(sanitised, {
      indent_size: 2,
      wrap_line_length: 80,
    });
    setBeautifiedHtml(beautified);
  }, [language.current, templateId]);

  return (
    <>
      <style>
        {`
          .ql-toolbar.ql-snow .ql-formats {
            display: inline-flex !important;
            margin-right: 0 !important;
            padding: 0 !important;
            gap: 0 !important;
          }
          .ql-toolbar.ql-snow button {
            width: auto !important;
          }
          .ql-toolbar.ql-snow .ql-formats .ql-bold,
          .ql-toolbar.ql-snow .ql-formats .ql-italic,
          .ql-toolbar.ql-snow .ql-formats .ql-list,
          .ql-toolbar.ql-snow .ql-formats .ql-link,
          .ql-toolbar.ql-snow .ql-formats .ql-clean,
          .ql-toolbar.ql-snow .ql-formats .ql-script {
            width: 20px !important;
            min-width: 20px !important;
            max-width: 20px !important;
          }
          .ql-container {
            min-height: 150px;
            max-height: 151px;
            overflow-y: auto;
            background-color: rgb(255 255 255);
          }
        `}
      </style>

      <Col lg={12}>
        <CustomFormItem
          className="form-item-label-with-drop-down"
          label={
            <LabelWithDropDown
              label={t("Property.MarketingDescription")}
              name={["landInfo", "templateId"]}
              value={templateId}
              onChange={val => setTemplateDescription(val)}
              options={templateList.map(template => ({ label: template.name, value: template.id }))}
              tooltip={lookupList[HELPER_TEXT_KEYS.building.marketingDescription]}
              templateId={templateId}
              changeHandler={val => {
                form.setFieldsValue({ landInfo: { templateId: val } });
                setTemplateDescription(val);
              }}
              form={form}
              tab={BUILDING_TABS_KEYS[7]}
              fieldName={FORM_FIELDS_LOOKUP.land.landInfo.marketingDescription.key}
              auditPanelDrawerVisible={auditPanelDrawerVisible}
            />
          }
          name={["descriptionTab", "marketingDescription"]}
          rules={[
            {
              required: BUILDING_REQUIRED_FIELDS_LOOKUP[fieldsType].descriptionTab.marketingDescription,
              message: t("LandInfo.MarketingDescriptionRulesMessage"),
            },
            { whitespace: true, message: t("LandInfo.MarketingDescriptionWhitespaceMessage") },
            {
              validator: (_, value) => validateMarketingDescription(value, t("Property.MarketingDescription")),
            },
          ]}
        >
          <>
            <Button
              type="text"
              icon={<img src={sourceIcon} alt="Source" style={{ width: 18 }} />}
              onClick={handleSourceIconClick}
              style={{ marginBottom: 8, float: "right" }}
            />
            {showHtml ? (
              <Input.TextArea
                rows={10}
                value={beautifiedHtml}
                onChange={e => setBeautifiedHtml(e.target.value)}
                style={{ width: "100%" }}
                placeholder="HTML Source"
              />
            ) : (
              <ReactQuill
                ref={quillRef}
                theme="snow"
                key={templateId}
                value={description || ""}
                onChange={handleEditorChange}
                style={{ minHeight: 150 }}
                modules={quillModules}
                formats={["bold", "italic", "list", "bullet", "link", "script"]}
                placeholder="Marketing Description"
              />
            )}
            {htmlError && <div className="error-msg">{htmlError}</div>}
          </>
        </CustomFormItem>
      </Col>

      <AppModal
        centered
        wrapClassName="modal-associated-properties"
        title={t("Template.Confirmation")}
        width={600}
        minHeight="10vh"
        maxHeight="60vh"
        overFlowY="auto"
        visible={modalVisible}
        onCancel={_onCancel}
        footer={
          <Row justify="center" gutter={24}>
            <Col xs={12} sm={12} md={8} lg={8}>
              <Button block onClick={_onCancel}>
                {t("Buttons.Cancel")}
              </Button>
            </Col>
            <Col xs={12} sm={12} md={8} lg={8}>
              <Button block type="primary" onClick={_onConfirm}>
                {t("Buttons.Confirm")}
              </Button>
            </Col>
          </Row>
        }
        destroyOnClose
      >
        <p>{t("Template.ConfirmationMessage")}</p>
      </AppModal>
    </>
  );
};

LandMarketingDescriptionEditor.propTypes = {
  viewOnlyMode: PropTypes.bool,
  tooltip: PropTypes.object,
  label: PropTypes.string,
  parentId: PropTypes.string,
  templateName: PropTypes.array,
  name: PropTypes.array,
  templateList: PropTypes.array.isRequired,
  form: PropTypes.object.isRequired,
  tab: PropTypes.string,
  fieldName: PropTypes.string,
  auditPanelDrawerVisible: PropTypes.bool,
  useRichTextEditor: PropTypes.bool,
};

export default LandMarketingDescriptionEditor;
